<template>
	<w-loader v-if="loading"/>
	<div v-else>
		<w-alert
			v-if="alert"
			:message="alert.message"
			:level="alert.level"
			class="mb-6"
		/>
		<w-form
			@submit="submited"
			@changed="may_submit = true"
			:readonly="readonly"
			data-discard="true"
			data-test-id="schedule-form"
		>
			<FormInput :label="l.t('app.name', 'Name')" :label_cols="2">
				<w-text-field
					v-if="readonly"
					v-model="schedule.name"
					not-clearable
					:rules="[$validation.required()]"
					hide-details='auto'
					maxLength="30"
					data-test-id="schedule-name-input"
				/>
				<w-text-field
					v-else
					v-model="schedule.name"
					:rules="[$validation.required()]"
					hide-details='auto'
					maxLength="30"
					data-test-id="schedule-name-input"
				/>
			</FormInput>
			<FormInput class="mt-4" :label="l.t('app.timezone', 'Timezone')" :label_cols="2">
				 <w-radio-group
					v-model="enable_timezone"
					@change="schedule.timezone = schedule.timezone || $session.user.account.timezone"
					row
					hide-details='auto'
					data-test-id="timezone-radio-buttons"
				>
					<v-radio
						:label="l.t('app.default', 'Default')"
						:value="false"
						data-test-id="schedule-default-tz-radio"
					></v-radio>
					<v-radio
						:label="l.t('app.custom', 'Custom')"
						:value="true"
						data-test-id="schedule-custom-tz-radio"
					></v-radio>
				</w-radio-group>
				<TimezoneSelector v-if="enable_timezone" v-model="schedule.timezone" data-test-id="schedule-timezone-selector"></TimezoneSelector>
			</FormInput>
			<FormInput :label="l.t('app.time-range', 'Time range')" :label_cols="2">
				<div
					v-for="(item, index) in grouped"
					:class="['time-slot-picker d-flex flex-wrap gap-10', {'last-item': index === grouped.length - 1 }]"
					:key="index"
				>
					<div class="gap-10 d-flex flex-column">
						<span class="light-bold">{{ l.t('app.days', 'Days') }}:</span>
						<div class="d-flex gap-5">
							<w-btn
								v-for="(value, day) in days"
								@click="addDay(item, day, item.items.find((item) => item.day === day))"
								class="day d-flex align-center justify-center light-bold"
								:class="{ selected: item.items.find((item) => item.day === day) }"
								:key="value"
								:data-test-id="`schedule-group-${index}-day-${day}-selector`"
								text
								icon
								changes-form
							>
								{{ day.substring(0, 1) }}
							</w-btn>
						</div>
					</div>
					<div class="time-settings d-flex flex-column align-center">
						<div class="d-flex align-center">
							<span class="light-bold">{{ l.t('schedules.24-hours', '24 hours?') }}</span>
							<InfoLink max-width="300">{{ l.t('schedules.from-midnight-to-the-next-day', 'From 12:00:00 Midnight to 11:59:59 the next day') }}</InfoLink>
						</div>
						<w-switch v-model="item.is24Hours" :data-test-id="`schedule-group-${index}-24-hours-toggle`"/>
					</div>
					<div class="item-block">
						<template v-if="!item.is24Hours">
							<div>
								<span class="light-bold">{{ l.t('app.from', 'From') }}:</span>
								<TimeSelector
									v-model="item.start"
									@customTime="add30MinuteDelay(item, $event)"
									:icons="false"
									:data-test-id="`schedule-group-${index}-start-time-selector`"
									class="w-100 time-selector"
								/>
							</div>
							<div>
								<span class="light-bold">{{ l.t('app.to', 'To') }}:</span>
								<TimeSelector
									v-model="item.end"
									:data-test-id="`schedule-group-${index}-end-time-selector`"
									:icons="false"
									class="w-100 time-selector"
								/>
							</div>
						</template>
						<div v-if="grouped.length > 1" class="mt-7">
							<w-tooltip no-carret>
								<template v-slot:activator>
									<w-btn
										@click="grouped.splice(index, 1)"
										data-test-id="schedule-add-group"
										color="#E85646"
										text
										icon
										changes-form
									>
										<v-icon>mdi mdi-close</v-icon>
									</w-btn>
								</template>
								<span>{{ $l.t('app.delete', 'Delete') }}</span>
							</w-tooltip>
						</div>
					</div>
				</div>
				<w-btn
					@click="grouped.push({ start: '09:00', end: '17:00', items: [] })"
					data-test-id="schedule-add-group"
					color="primary"
					class="light-bold pl-0 mt-2"
					text
					changes-form
				>
					<v-icon class="mr-1">mdi-plus</v-icon>{{ l.t('schedules.add-new-time-slot', 'Add New Time Slot') }}
				</w-btn>
			</FormInput>
			<FormInput :label="l.t('schedules.observed-holidays', 'Observed Holidays')" :label_cols="2">
				<div class="d-flex align-center observe-holidays">
					<w-switch v-model="observed_holidays" @change="observeHolidays" data-test-id="schedule-observed-holidays" :key="selected_holidays.length"/>
					<InfoLink max-width="300">{{ l.t('schedules.add-preset-and-custom-holiday-dates', 'Add preset and custom holiday dates') }}</InfoLink>
				</div>
				<template v-if="observed_holidays">
					<div
						v-for="(item, index) in selected_holidays"
						:class="['time-slot-picker d-flex flex-wrap gap-10', {'last-item': index === selected_holidays.length - 1 }]"
						:key="index"
					>
						<div class="holiday-selector w-100">
							<span class="light-bold">{{ l.t('app.holiday', 'Holiday') }}:</span>
							<w-select
								v-model="item.holiday_id"
								:items="holidays.map((x) => {
									return { value: x.id, text: x.name }
								})"
								:rules="[$validation.required()]"
								:placeholder="l.t('schedules.select-a-holiday', 'Select a holiday')"
								hide-details="auto"
								:data-test-id="`schedule-group-${index}-holidays-select`"
							/>
						</div>
						<div class="time-settings d-flex flex-column align-center">
							<span class="light-bold">{{ l.t('schedule.repeat-annually', 'Repeat annually?') }}</span>
							<w-switch :value="item.use_once" @change="item.use_once = !$event" :data-test-id="`schedule-holiday-group-${index}-repeat-annually`"/>
						</div>
						<div class="item-block">
							<div>
								<span class="light-bold">{{ l.t('app.hours', 'Hours') }}:</span>
								<TimeSelector
									v-model="item.start"
									:icons="false"
									:data-test-id="`schedule-holiday-group-${index}-start-time-selector`"
									class="w-100 time-selector"
								/>
							</div>
							<div class="mt-18">
								<TimeSelector
									v-model="item.end"
									:icons="false"
									:data-test-id="`schedule-holiday-group-${index}-end-time-selector`"
									class="w-100 time-selector"
								/>
							</div>
						</div>
						<div v-if="item.holiday_id !== -1" class="item-block">
							<div class="holiday-delay">
								<div class="d-flex align-center">
									<span class="light-bold">{{ l.t('app.days-before', 'Days before') }}:</span>
									<InfoLink max-width="300">{{ l.t('schedules.add-additional-days-your-company-is-closed-prior', 'Add additional days your company is closed prior to the date of the observed holiday.') }}</InfoLink>
								</div>
								<w-text-field
									v-model="item.days_before"
									not-clearable
									:rules="[$validation.numeric()]"
									type="number"
									hide-details='auto'
									maxLength="30"
									:data-test-id="`schedule-holiday-group-${index}-days-before`"
								/>
							</div>
							<div class="holiday-delay">
								<div class="d-flex align-center">
									<span class="light-bold">{{ l.t('app.days-after', 'Days after') }}:</span>
									<InfoLink max-width="300">{{ l.t('schedules.add-additional-days-your-company-is-closed-following', 'Add additional days your company is closed following the dates of the observed holiday') }}</InfoLink>
								</div>
								<w-text-field
									v-model="item.days_after"
									not-clearable
									:rules="[$validation.numeric()]"
									type="number"
									hide-details='auto'
									maxLength="30"
									:data-test-id="`schedule-holiday-group-${index}-days-after`"
								/>
							</div>
						</div>
						<div v-else class="item-block">
							<div class="date-picker">
								<span class="light-bold">{{ l.t('app.start-date', 'Start date') }}:</span>
								<DatePickerInput
									:label="l.t('app.start-date', 'Start date')"
									:value="formatDate(item.start_date)"
									:rules="[$validation.required()]"
									@changed="item.start_date = formatToEpoch($event)"
									:key="item.start_date"
									:data-test-id="`schedule-holiday-group-${index}-start-day-input`"
									icon="$vuetify.icons.calendar"
								/>
							</div>
							<div class="date-picker">
								<span class="light-bold">{{ l.t('app.end-date', 'End date') }}:</span>
								<DatePickerInput
									:label="l.t('app.end-date', 'End date')"
									:value="formatDate(item.end_date)"
									:rules="[$validation.required()]"
									@changed="item.end_date = formatToEpoch($event)"
									:key="item.end_date"
									:data-test-id="`schedule-holiday-group-${index}-end-date-input`"
									icon="$vuetify.icons.calendar"
								/>
							</div>
						</div>
						<div class="mt-7">
							<w-tooltip no-carret>
								<template v-slot:activator>
									<w-btn
										@click="deleteHoliday(index)"
										:data-test-id="`schedule-holiday-group-${index}-delete`"
										color="#E85646"
										text
										icon
										changes-form
									>
										<v-icon>mdi mdi-close</v-icon>
									</w-btn>
								</template>
								<span>{{ $l.t('app.delete', 'Delete') }}</span>
							</w-tooltip>
						</div>
					</div>
					<w-btn
						@click="selected_holidays.push({ ...empty_item })"
						color="primary"
						class="light-bold pl-0 mt-2 cursor-pointer"
						data-test-id="schedule-holiday-add-group"
						text
						changes-form
					>
						<v-icon class="mr-1">mdi-plus</v-icon>{{ l.t('schedule.add-another-holiday', 'Add another holiday') }}
					</w-btn>
				</template>
			</FormInput>
			<DefaultModal
				v-model="show_error_modal"
				@close="show_error_modal = false"
				width="500"
				data-test-id="schedule-error-modal"
			>
				<template v-slot:title>
					{{l.t('app.error', 'Error')}}
				</template>
				{{ error }}
				<template v-slot:buttons>
					<w-btn color="secondary" @click="show_error_modal = false" data-test-id="schedule-error-close-btn">{{l.t('app.close', 'Close')}}</w-btn>
				</template>
			</DefaultModal>
			<w-btn type="submit" class="primary d-flex mx-auto" :disabled="!may_submit" data-test-id="schedule-submit-btn">{{ l.t('app.save', 'Save') }}</w-btn>
		</w-form>
	</div>
</template>

<script>
import l from '../../libs/lang';
import InfoLink from '../elements/InfoLink.vue';
import FormInput from '../elements/form/FormInput.vue';
import TimeSelector from '../elements/form/TimeSelector.vue';
import DefaultModal from '../elements/modal/DefaultModal.vue';
import TimezoneSelector from '../elements/TimezoneSelector.vue';
import DatePickerInput from '../elements/form/DatePickerInput.vue';

export default {
	props: ['_schedule', 'readonly'],
	components: {
		InfoLink,
		FormInput,
		DefaultModal,
		TimeSelector,
		DatePickerInput,
		TimezoneSelector,
	},
	data() {
		return {
			l,
			loading: false,
			observed_holidays: false,
			selected_holidays: [],
			alert: null,
			schedule: null,
			enable_timezone: false,
			days: {
				'Su': l.t('schedules.sunday ', 'Sunday'),
				'M': l.t('schedules.monday', 'Monday'),
				'T': l.t('schedules.tuesday', 'Tuesday'),
				'W': l.t('schedules.wednesday', 'Wednesday'),
				'Th': l.t('schedules.thursday', 'Thursday'),
				'F': l.t('schedules.friday', 'Friday'),
				'S': l.t('schedules.saturday', 'Saturday'),
			},
			holidays: [
				{ id: -1, name: l.t('app.custom', 'Custom') },
				{ id: 1, name: l.t('schedules.new-years-day', 'New Years Day') },
				{ id: 2, name: l.t('schedules.martin-luther-king-day', 'Martin Luther King Day') },
				{ id: 3, name: l.t('schedules.presidents-day', 'Presidents Day') },
				{ id: 4, name: l.t('schedules.memorial-day', 'Memorial Day') },
				{ id: 5, name: l.t('schedules.independence-day', 'Independence Day') },
				{ id: 6, name: l.t('schedules.labor-day', 'Labor Day') },
				{ id: 7, name: l.t('schedules.columbus-day', 'Columbus Day') },
				{ id: 8, name: l.t('schedules.veterans-day', 'Veterans Day') },
				{ id: 9, name: l.t('schedules.thanksgiving', 'Thanksgiving') },
				{ id: 10, name: l.t('schedules.black-friday', 'Black Friday') },
				{ id: 11, name: l.t('schedules.christmas-eve', 'Christmas Eve') },
				{ id: 12, name: l.t('schedules.Christmas', 'Christmas') },
				{ id: 13, name: l.t('schedules.new-years-eve', 'New Years Eve') },
			],
			error: null,
			show_error_modal: false,
			may_submit: false,
			default_form: {
				schedule: {},
				enable_timezone: null
			},
			grouped: [],
		};
	},
	created() {
		this.$data.loading = true;
		this.$data.schedule = this.$props._schedule;

		if (!this.$data.schedule.id) {
			this.$data.schedule.items = this.$data.schedule.items.filter((item) => !['S', 'Su'].includes(item.day)).map((item) => ({
				...item,
				start_hour: 9,
				start_minute: 0,
				end_hour: 17,
				end_minute: 0,
			}));
		} else {
			this.$data.selected_holidays = this.$data.schedule.items.filter((item) => item.holiday_id || item.start_date || item.end_date).map((holiday) => ({
				...holiday,
				holiday_id: holiday?.holiday_id || -1,
				start: `${this.add_zero_if_needed(holiday.start_hour)}:${this.add_zero_if_needed(holiday.start_minute)}`,
				end: `${this.add_zero_if_needed(holiday.end_hour)}:${this.add_zero_if_needed(holiday.end_minute)}`,
			}));
			this.$data.schedule.items = Object.values(
				this.$data.schedule.items.filter((item) => !(item.holiday_id || item.start_date || item.end_date)).reduce((acc, item) => {
					if (!acc[item.day] || item.rank < acc[item.day].rank) {
						acc[item.day] = item;
					}
					return acc;
				}, {})
			);
		}

		this.$data.observed_holidays = this.$data.selected_holidays.length > 0;
		this.$data.enable_timezone = !!this.$data.schedule.timezone;
		this.$data.default_form.schedule = JSON.parse(JSON.stringify(this.$data.schedule));
		this.$data.default_form.enable_timezone = this.$data.enable_timezone;
		this.$data.grouped = this.$data.schedule.items.reduce((acc, item) => {
			const start = `${this.add_zero_if_needed(item.start_hour)}:${this.add_zero_if_needed(item.start_minute)}`;
			const end = `${this.add_zero_if_needed(item.end_hour)}:${this.add_zero_if_needed(item.end_minute)}`;
			const is24Hours = start === '00:00' && end === '23:59';
			const existingGroup = acc.find((group) => group.start === start && group.end === end);
			if (existingGroup) {
				existingGroup.items.push(item);
			} else {
				acc.push({
 start, end, items: [item], is24Hours
});
			}
			return acc;
		}, []);
		this.$data.loading = false;
	},
	methods: {
		deleteHoliday(index) {
			this.$data.selected_holidays.splice(index, 1);
			if (!this.$data.selected_holidays.length) this.$data.observed_holidays = false;
		},
		addDay(group, day, exists) {
			for (const item of this.$data.grouped) {
				item.items = item.items.filter((x) => x.day !== day);
			}
			if (!exists) {
				const item = group.items.length ? JSON.parse(JSON.stringify(group.items[0])) : { ...this.empty_item };
				item.day = day;
				item.id = null;
				group.items.push(item);
			}
		},
		observeHolidays() {
			setTimeout(() => {
				if (!this.$data.selected_holidays.length) {
					this.$data.selected_holidays.push({ ...this.empty_item });
				}
			}, 50);
		},
		formatDate(date) {
			if (!date) return null;
			const d = new Date(date * 1000);
			return `${d.getFullYear()}-${this.add_zero_if_needed(d.getMonth() + 1)}-${this.add_zero_if_needed(d.getDate())}`;
		},
		formatToEpoch(val) {
			if (!val) return null;
			return Math.floor(new Date(val).getTime() / 1000);
		},
		add30MinuteDelay(item, val) {
			let [hours, minutes] = val.split(':');
			hours = parseInt(hours, 10);
			minutes = parseInt(minutes, 10);
			minutes += 30;
			if (minutes > 60) {
				minutes -= 60;
				hours++;
				if (hours === 24) {
					hours = 23;
					minutes = 59;
				}
			}
			item.end = `${this.add_zero_if_needed(hours)}:${this.add_zero_if_needed(minutes)}`;
		},
		add_zero_if_needed(val) {
			return val <= 9 ? `0${val}` : val;
		},
		// eslint-disable-next-line consistent-return
		async submited(e) {
			this.prepareItemsForUpdating();
			const payload = { ...this.$data.schedule };
			for (const item of payload.items) {
				if (item.start_hour > item.end_hour || (item.start_hour === item.end_hour && item.start_minute > item.end_minute)) {
					this.alert = {
						message: l.t('schedules.incorrect-time-range-is-set-for-some-days', 'Incorrect time range is set for some days!'),
						level: 'error'
					};
					return setTimeout(() => { this.alert = null; }, 5000);
				}
				if (item.start_date > item.end_date) {
					this.alert = {
						message: l.t('schedules.incorrect-time-range-is-set-for-some-holidays', 'Incorrect time range is set for some holidays!'),
						level: 'error'
					};
					return setTimeout(() => { this.alert = null; }, 5000);
				}
			}
			if (e) e.preventDefault();
			this.$emit('submited', payload);
		},
		prepareItemsForUpdating() {
			const all_days = [
				...this.$data.grouped.flatMap((group) => group.items.map((item) => this.prepareItems(item, group.start, group.end, group.is24Hours)))
			];
			const holidays = this.observed_holidays
				? this.$data.selected_holidays.map((item) => {
					const itemCopy = { ...item };
					return this.prepareItems(itemCopy, item.start, item.end);
				})
				: [];
			this.$data.schedule.timezone = this.$data.enable_timezone ? this.$data.schedule.timezone : null;
			this.$data.schedule.items = [...all_days, ...holidays];
		},
		prepareItems(item, start, end, is24Hours = false) {
			const [start_hour, start_minute] = start.split(':');
			const [end_hour, end_minute] = end.split(':');
			item.start_date = item.holiday_id === -1 ? item.start_date : null;
			item.end_date = item.holiday_id === -1 ? item.end_date : null;
			item.start_hour = is24Hours ? 0 : parseInt(start_hour);
			item.start_minute = is24Hours ? 0 : parseInt(start_minute);
			item.end_hour = is24Hours ? 23 : parseInt(end_hour);
			item.end_minute = is24Hours ? 59 : parseInt(end_minute);
			item.id = null;
			item.days_before = item.days_before && item.holiday_id !== -1 ? parseInt(item.days_before) : null;
			item.days_after = item.days_after && item.holiday_id !== -1 ? parseInt(item.days_after) : null;
			// eslint-disable-next-line no-nested-ternary
			item.holiday_id = item.holiday_id ? (item.holiday_id === -1 ? null : item.holiday_id) : null;
			item.rank = 9999;
			delete item.start;
			delete item.end;
			return item;
		},
	},
	computed: {
		empty_item() {
			return {
				holiday_id: null,
				days_before: null,
				days_after: null,
				start_date: null,
				end_date: null,
				start_hour: 9,
				start_minute: 0,
				start: '09:00',
				end: '17:00',
				end_hour: 17,
				end_minute: 0,
				voip_destination_id: 0,
				use_once: null,
				expiration_date: null
			};
		}
	},
	watch: {
		error(val) {
			this.$data.show_error_modal = !!val;
		}
	}
};
</script>

<style scoped lang="scss">
.observe-holidays {
	::v-deep span {
		margin-left: -15px;
		margin-top: 1px;
	}
}
.light-bold {
	font-weight: 600;
}
.mt-18 {
	margin-top: 18px;
}
.time-slot-picker {
	width: fit-content;
	padding-bottom: 15px;
}
.last-item {
	border-bottom: 1px solid #85929AA6;
}
.gap-5 {
	gap: 5px;
}
.gap-10 {
	gap: 10px;
}
.day {
	width: 30px;
	height: 30px;
	background-color: #ccc;
	color: white !important;
	letter-spacing: 1.5px !important;
}
.day.selected {
	background-color: var(--v-info-base);
}
.time-settings {
	span {
		min-width: max-content;
	}
	> .custom-switch {
		width: 30px;
	}
}
.time-selector {
	max-width: 110px !important;
}
.holiday-delay {
	max-width: 107px;
}
.date-picker {
	max-width: 120px;
}
.holiday-selector {
	max-width: 170px !important;
}
.item-block {
	display: flex;
	gap: 20px;
}
</style>
